/**
 * React-Query hooks for the operating-calendar config UI.
 *
 * One calendar per scope (org default or site) plus three child collections
 * (hours / exceptions / scheduled events) and a read-only resolved-state
 * preview. Mutations invalidate the owning calendar's child lists so the
 * editor reflects writes immediately.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getOperatingCalendars,
  getOperatingCalendarById,
  getEffectiveCalendarForSchool,
  resolveSiteState,
  createOperatingCalendar,
  updateOperatingCalendar,
  deleteOperatingCalendar,
  getOperatingHours,
  createOperatingHours,
  updateOperatingHours,
  deleteOperatingHours,
  getOperatingExceptions,
  createOperatingException,
  updateOperatingException,
  deleteOperatingException,
  getScheduledSiteEvents,
  createScheduledSiteEvent,
  updateScheduledSiteEvent,
  deleteScheduledSiteEvent,
  type OperatingCalendarFilters,
  type OperatingCalendarWriteInput,
  type OperatingHoursWriteInput,
  type OperatingExceptionWriteInput,
  type ScheduledSiteEventWriteInput,
} from '../../services';

export const operatingCalendarKeys = {
  all: ['operatingCalendars'] as const,
  lists: () => [...operatingCalendarKeys.all, 'list'] as const,
  list: (filters: OperatingCalendarFilters) =>
    [...operatingCalendarKeys.lists(), filters] as const,
  details: () => [...operatingCalendarKeys.all, 'detail'] as const,
  detail: (id: number) => [...operatingCalendarKeys.details(), id] as const,
  effective: (schoolId: number) =>
    [...operatingCalendarKeys.all, 'effective', schoolId] as const,
  resolved: (schoolId: number) =>
    [...operatingCalendarKeys.all, 'resolved', schoolId] as const,
  hours: (calendarId: number) =>
    [...operatingCalendarKeys.all, 'hours', calendarId] as const,
  exceptions: (calendarId: number) =>
    [...operatingCalendarKeys.all, 'exceptions', calendarId] as const,
  events: (calendarId: number) =>
    [...operatingCalendarKeys.all, 'events', calendarId] as const,
};

// --- Calendar -------------------------------------------------------------

export function useOperatingCalendars(filters: OperatingCalendarFilters) {
  return useQuery({
    queryKey: operatingCalendarKeys.list(filters),
    queryFn: () => getOperatingCalendars(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useOperatingCalendar(id: number) {
  return useQuery({
    queryKey: operatingCalendarKeys.detail(id),
    queryFn: () => getOperatingCalendarById(id),
    enabled: !!id,
  });
}

export function useEffectiveCalendar(schoolId: number | undefined) {
  return useQuery({
    queryKey: operatingCalendarKeys.effective(schoolId ?? 0),
    queryFn: () => getEffectiveCalendarForSchool(schoolId as number),
    enabled: !!schoolId,
  });
}

export function useResolvedSiteState(schoolId: number | undefined) {
  return useQuery({
    queryKey: operatingCalendarKeys.resolved(schoolId ?? 0),
    queryFn: () => resolveSiteState(schoolId as number),
    enabled: !!schoolId,
  });
}

export function useCreateOperatingCalendar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OperatingCalendarWriteInput) =>
      createOperatingCalendar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatingCalendarKeys.all });
    },
  });
}

export function useUpdateOperatingCalendar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<OperatingCalendarWriteInput>;
    }) => updateOperatingCalendar(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: operatingCalendarKeys.all });
      queryClient.invalidateQueries({
        queryKey: operatingCalendarKeys.detail(id),
      });
    },
  });
}

export function useDeleteOperatingCalendar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteOperatingCalendar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: operatingCalendarKeys.all });
    },
  });
}

// --- Operating hours ------------------------------------------------------

export function useOperatingHours(calendarId: number | undefined) {
  return useQuery({
    queryKey: operatingCalendarKeys.hours(calendarId ?? 0),
    queryFn: () => getOperatingHours(calendarId as number),
    enabled: !!calendarId,
  });
}

export function useCreateOperatingHours() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OperatingHoursWriteInput) => createOperatingHours(data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: operatingCalendarKeys.hours(vars.calendar),
      });
    },
  });
}

export function useUpdateOperatingHours(calendarId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<OperatingHoursWriteInput>;
    }) => updateOperatingHours(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: operatingCalendarKeys.hours(calendarId),
      });
    },
  });
}

export function useDeleteOperatingHours(calendarId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteOperatingHours(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: operatingCalendarKeys.hours(calendarId),
      });
    },
  });
}

// --- Operating exceptions -------------------------------------------------

export function useOperatingExceptions(calendarId: number | undefined) {
  return useQuery({
    queryKey: operatingCalendarKeys.exceptions(calendarId ?? 0),
    queryFn: () => getOperatingExceptions(calendarId as number),
    enabled: !!calendarId,
  });
}

export function useCreateOperatingException() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: OperatingExceptionWriteInput) =>
      createOperatingException(data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: operatingCalendarKeys.exceptions(vars.calendar),
      });
    },
  });
}

export function useUpdateOperatingException(calendarId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<OperatingExceptionWriteInput>;
    }) => updateOperatingException(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: operatingCalendarKeys.exceptions(calendarId),
      });
    },
  });
}

export function useDeleteOperatingException(calendarId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteOperatingException(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: operatingCalendarKeys.exceptions(calendarId),
      });
    },
  });
}

// --- Scheduled site events ------------------------------------------------

export function useScheduledSiteEvents(calendarId: number | undefined) {
  return useQuery({
    queryKey: operatingCalendarKeys.events(calendarId ?? 0),
    queryFn: () => getScheduledSiteEvents(calendarId as number),
    enabled: !!calendarId,
  });
}

export function useCreateScheduledSiteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ScheduledSiteEventWriteInput) =>
      createScheduledSiteEvent(data),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({
        queryKey: operatingCalendarKeys.events(vars.calendar),
      });
    },
  });
}

export function useUpdateScheduledSiteEvent(calendarId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<ScheduledSiteEventWriteInput>;
    }) => updateScheduledSiteEvent(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: operatingCalendarKeys.events(calendarId),
      });
    },
  });
}

export function useDeleteScheduledSiteEvent(calendarId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteScheduledSiteEvent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: operatingCalendarKeys.events(calendarId),
      });
    },
  });
}
